// @flow
// ----------------------------------------------------------------------
// <copyright file='Izenda.ReportViewer.js' company='Izenda'>
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { action, observable, observe } from 'mobx';
import React, { Component } from 'react';
import { get, isEmpty, isNil, getOr, isEqual, map, some } from 'lodash/fp';
import PropTypes from 'prop-types';

import connect from 'share/enhancers/Izenda.Connect';
import classnames from 'classnames';
import ReportDesignerStore from 'logic/Izenda.ReportDesignerStore';
import AuthorizationStore from 'logic/Izenda.AuthorizationStore';
import ReportDesignerFieldsStore from 'logic/Izenda.ReportDesignerFieldsStore';
import ReportDesignerDataSourceStore from 'logic/Izenda.ReportDesignerDataSourceStore';
import reportDesignerConstants from 'logic/data/Izenda.ReportDesignerConstants';
import reportsConstants from 'logic/data/Izenda.ReportsConstants';
import ReportViewerToolbar from './Izenda.ReportViewerToolbar';
import ReportViewerHeader from './Izenda.ReportViewerHeader';
import ReportViewerFilters from './Izenda.ReportViewerFilters';
import ReportDesignerFilters from 'modules/ReportDesigner/Fields/Filter/Izenda.Filters';
import RightContentFieldDetail from 'modules/ReportDesigner/Fields/Izenda.RightContentFieldDetail';
import ReportViewerContent from './Izenda.ReportViewerContent';
import GroupPanel from 'share/components/controls/Izenda.GroupPanel';
import subReportLink from 'share/utils/Izenda.SubReportLink';
import SubReportUtils from 'share/utils/Izenda.SubReportUtils';
import ReportSubscribe from 'modules/Reports/ReportSubscribe/Izenda.ReportSubscribe';
import actions from './Izenda.ReportViewerActions';
import Alert from 'share/components/controls/Izenda.Alert';
import UiState from 'logic/Izenda.UiState';
import Button from 'share/components/controls/Izenda.Button';
import routeUtils from 'share/utils/Izenda.RouteUtils';
import { ACCESS_RIGHT } from 'logic/data/Izenda.AuthorizationConstants';
import LicenseCheckerConstants from 'logic/data/Izenda.LicenseCheckerConstants';
import DnDFilterItemConfig from 'modules/ReportDesigner/Fields/Filter/Izenda.DnDFilterItemConfig';
import Dimensions from 'share/components/hoc/Izenda.Dimensions';
import CachingConfigurationStore from 'logic/Izenda.CachingConfigurationStore';

@connect(
  actions,
  Dimensions
)
export default class ReportViewer extends Component {
  @observable
  viewerContentHeight;
  @observable
  footerHeight;
  @observable
  isCacheEnable = false;
  static propTypes = {
    store: PropTypes.object,
    editMode: PropTypes.bool
  };

  constructor(props: any) {
    super(props);
    this.startTime = new Date().getTime();
    this.initState(props);
  }


  componentWillReceiveProps(nextProps: any) {
    // This function is not call when change from VIEW MODE to EDIT MODE opposite, they use different component
    // for when react router change history for these case:
    // SAVE AS : need to reload data with new ID, name, category/sub category
    // MOVE : need to reload data with the same ID but have different name,cateogry, sub catogry
    // OPEN SUB REPORT: differenct report ID,
    // CHANGE VERSION OF REPORT: reload report definition

    // from report list to report viewer
    const fromReportList = get('state.fromList', nextProps.location);
    // from report designer
    const fromReportDesigner = get('state.fromReportDesigner', nextProps.location);
    // from quick edit
    const fromQuickEdit = get('state.fromQuickEdit', nextProps.location);

    const currentPathName = get('location.pathname', this.props);
    const nextPathName = get('location.pathname', nextProps);

    if (currentPathName !== nextPathName && !fromReportList && !fromReportDesigner && !fromQuickEdit) {
      this.initState(nextProps);
      return this.init(nextProps);
    }
  }


  componentDidMount() {
    this.rotateLandscape();
  };

  /**
   * Init state
   * @param {Object} props
   */
  initState(props: any): void {

    const { store, params, location, editMode } = props;
    const paramID = this.getParamID(params.id);
    // Set tenantId for selected.
    // TODO: Technical debt: set tenant id in router
    const tenantId = get('query.tenantId', location);
    if (!isEmpty(tenantId)) {
      UiState.setTenantId(tenantId, true);
    }

    if (
      store.selectedReportId !== paramID ||
      (store.selectedReportId === paramID && store.version !== params.version)
    ) {
      store.resetModel();
    }

    // Init report designer store
    this.masterStore = new ReportDesignerStore(true, paramID, store.report, null, editMode);

    this.authorizationStore = new AuthorizationStore();
    this.reportDesignerFieldsStore = new ReportDesignerFieldsStore(this.masterStore, true);
    this.reportDesignerDataSource = new ReportDesignerDataSourceStore(this.masterStore);
    this.cachingConfiguration = new CachingConfigurationStore();


    this.observeNeedUpdateResult();

    store.updateReportId(paramID, params.version);
    store.model.resetReportList();
  }

  /**
   * Init
   * @param {Object} nextProps
   */
  init(nextProps: any, isCancelled: boolean): any {
    const props = nextProps || this.props;
    const { store, loader } = props;
    const id = this.getParamID(get('params.id', props));

    loader.startLoading();
    return Promise.join(
      this.masterStore.validateReportId(true),
      this.authorizationStore.loadAccessPriority(id),

      action((validateResult, reportAccessRight) => {
        this.masterStore.setAccessPriority(reportAccessRight);
        this.cachingConfiguration.loadCachingConfiguration(false).then((data) => {
          this.isCacheEnable = getOr(false, 'dataCacheSetting.isEnableDataCache', data);
        });

        return (
          validateResult &&
          this.masterStore
            .cancelChanges()
            .then(() => Promise.all([this.loadInitialData(props, store.version, isCancelled), this.masterStore.init()]))
        );
      })
    ).finally(loader.endLoading.bind(loader));
  }

  rotateLandscape() {
    var self = this;          // Store `this` component outside the callback
    if ('onorientationchange' in window) {
      window.addEventListener("orientationchange", function () {
        // `this` is now pointing to `window`, not the component. So use `self`.
        self.setState({
          orientation: !self.state.orientation
        });
      }, false);
    }
  }

  getParamID(paramID) {
    let id = paramID;
    if (paramID && paramID.indexOf('?') > 0) {
      id = paramID.split('?')[0];
    }
    return id;
  }

  handleNeedUpdateResultChanged = (change: boolean) => {
    this.props.handleNeedUpdateResultChanged(change, this.masterStore.alertDetails);
  };

  componentDidMount() {
    this.cascadingChangedDisposer = observe(
      this.masterStore,
      'showCascadingChangedMessage',
      this.props.handleCascadingChanged
    );

    this.needUpdateResultChangedDisposer = observe(
      this.masterStore,
      'needUpdateResult',
      this.handleNeedUpdateResultChanged
    );

    this.init().then(() => {
      const renderingTime = new Date().getTime() - this.startTime;
      return this.props.store.updateRenderingTime(renderingTime);
    });
    this.updateViewerContentHeight();
  }

  /**
   * Observe need update result to show info message
   */
  observeNeedUpdateResult() {
    // Remove old observe
    this.needUpdateResultChangedDisposer && this.needUpdateResultChangedDisposer();

    this.needUpdateResultChangedDisposer = observe(
      this.masterStore,
      'needUpdateResult',
      this.handleNeedUpdateResultChanged
    );
  }

  componentWillUnmount() {
    // Terminate all report part worker if leave report viewer
    this.reportDesignerFieldsStore.terminateAllReportPartWorker();

    // Store report model in ReportStore
    this.props.store.readReport(this.masterStore.model);
    this.cascadingChangedDisposer && this.cascadingChangedDisposer();
    this.needUpdateResultChangedDisposer && this.needUpdateResultChangedDisposer();
    this.masterStore.destroy();
  }

  /**
   * Load initial data
   * @param {Object} props
   * @param {number} version
   */
  loadInitialData(props: any, version: number, isCancelled: boolean): any {
    const { store, params, location } = props;
    const paramID = this.getParamID(params.id);
    const fieldStore = this.reportDesignerFieldsStore;
    const masterStore = this.masterStore;
    const query = location.query;
    const pageId = getOr(null, 'state.page', location);

    if (get('tempUrl', query)) {
      this.subReportFilter = subReportLink.readSubReportQuery(query);
      this.masterStore.model.setSubReportFilters(this.subReportFilter, false);
    } else {
      this.subReportFilterFromQuery = subReportLink.convertQueryToFilter(query);
      this.masterStore.model.setSubReportFilters(this.subReportFilterFromQuery, true);
    }

    store.updateReportId(paramID, params.version);
    // Reset Subscription mode
    this.masterStore.model.displayMode.reset();
    const promise = this.subReportFilterFromQuery
      ? SubReportUtils.resolveSubReportFilter(this.subReportFilterFromQuery)
      : Promise.resolve(this.subReportFilter);

    return promise
      .then(subReportFilter => {
        this.masterStore.model.setSubReportFilters(subReportFilter, false);
        return null;
      })
      .then(() => {
        return !store.report
          ? this.masterStore.loadReportDesigner(store.version, null, pageId, isCancelled)
          : Promise.resolve();
      })
      .then(result => {
        const reportDef = result || {};
        const noPermission =
          !isNil(reportDef) &&
          !isNil(reportDef.messages) &&
          !isEmpty(reportDef.messages[0].detail) &&
          reportDef.messages[0].detail === 'NoPermission';
        if (!isEmpty(reportDef)) {
          // Update report relationship
          this.masterStore.model.dataSource.readDataSourceGrid(get('reportRelationship', reportDef));

          // These following lines are copied from the function getReportInfo in ReportStore
          store.reportType = getOr(reportsConstants.CATEGORY_TYPE.REPORT, 'type', reportDef).toString();
          store.reportName = getOr('', 'name', reportDef);
          store.categoryId = reportDef.categoryId || reportsConstants.EMPTY_GUID;
          !store.selectedCategoryId && (store.selectedCategoryId = reportDef.subCategoryId || reportDef.categoryId);

          const subReport = {
            selectedReport: paramID,
            masterReportId: query.masterReportId,
            reportFilter: query.inheritFilter
          };
          fieldStore.validateFilterFieldsTypeSubReport(subReport).then(result => {
            const error = getOr(null, 'messages[0]', result);
            if (!isNil(error)) {
              const commonError = props.localization.getText('COMMON_MESSAGES_GENERIC_ERROR');
              masterStore.alertDetails.set({
                success: false,
                errors: [{ message: getOr(commonError, 'messages[0]', error) }]
              });
            } else if (result) {
              masterStore.alertDetails.set({
                success: true,
                message: props.localization.getText('REPORTDESIGNER_FILTER_LOGIC_REPORT')
              });
              fieldStore.reportFilters.setLogic('');
            }
          });
        }
        const {
          reportFilter: { filterFields }
        } = result;
        const isDirtyReportFilter = some('isOveridingInheritedFilter', filterFields);
        isDirtyReportFilter && this.masterStore.model.markDirty();
        return !noPermission && props.detectReportChange(this.masterStore);
      })
      .then(success => {
        return success
          ? Promise.join(
            fieldStore.getFilterData(store.version, null, null, null, props.editMode),
            fieldStore.loadAvailableQuerySourceFields(masterStore.reportId)
          )
          : success;
      });
  }

  /**
   * Update viewer content height
   */
  @action
  updateViewerContentHeight = (): void => {
    if (this.viewerNode) {
      const { top, height } = this.viewerNode.getBoundingClientRect();
      const newFooterHeight = window.innerHeight - top - height;
      newFooterHeight !== this.footerHeight && (this.footerHeight = newFooterHeight);
    }

    if (this.viewerContentNode) {
      const { top } = this.viewerContentNode.getBoundingClientRect();
      const newViewerContentHeight = window.innerHeight - top - this.footerHeight;
      newViewerContentHeight !== this.viewerContentHeight && (this.viewerContentHeight = newViewerContentHeight);
    }
  };

  /**
   * Update all report parts
   */
  updateReportParts = (ignoreCache: boolean = false): any => () => {
    const loader = this.props.loader;
    const fieldStore = this.reportDesignerFieldsStore;
    loader.startLoading();
    let updatePromise;
    if (!this.props.editMode) {
      fieldStore.model.reportFilter.updateFilterInfoForSubReport();

      // Reset the separator expanded values.
      const reportParts = getOr([], 'model.reportBody.reportParts', fieldStore);
      map(part => {
        const reportInfo = part.reportInfo || [];
        map(data => {
          if (data.separatorInfo) {
            data.separatorInfo.isExpand = true;
          }
        }, reportInfo)
      }, reportParts);

      updatePromise = fieldStore.updateReportsParts(ignoreCache);
    } else {
      updatePromise = this.masterStore
        .saveDraftReport(reportDesignerConstants.PAGE_NAME.FIELDS, true)
        .then(() => fieldStore.updateReportsParts(ignoreCache));
    }

    return updatePromise.finally(() => {
      loader.endLoading();
    });
  };

  /**
   * Remove cross filtering
   * @param {Object} pill selected pill
   */
  removeCrossFiltering = (pill: any): void => {
    this.reportDesignerFieldsStore.removePill(pill);
  };

  /**
   * Remove all cross filtering
   */
  removeAllCrossFiltering = (): void => {
    this.reportDesignerFieldsStore.removeAllPills();
  };

  /**
   * Reset default value
   */
  resetDefaultValue = (): void => {
    this.reportDesignerFieldsStore.resetCrossFilterDefaultValue();
  };

  /**
   * Render report filter
   */
  renderReportFilter(): any {
    const fieldsStore = this.reportDesignerFieldsStore;
    const reportBody = fieldsStore.reportBody;
    const accessPriority = get('reportStore.model.accessPriority', fieldsStore);
    const props = this.props;
    const { localization } = props;
    const displayMode = fieldsStore.reportStore.model.displayMode;
    const doesDisableUpdateResultBtn = get('doesDisableUpdateResultBtn', displayMode);
    const updateResultInfo = {
      updateResults: props.updateResults(fieldsStore, this.updateReportParts(false)),
      doesDisableUpdateResultBtn,
      localization,
      accessPriority
    };
    return props.editMode ? (
      <GroupPanel className='izenda-FilterGroupPanel u_noMarginAll'>
        <ReportDesignerFilters
          store={fieldsStore}
          viewMode={false}
          editMode={props.editMode}
          uiStore={DnDFilterItemConfig.targetOptions}
          afterToggled={this.updateViewerContentHeight}
          afterMove={this.updateViewerContentHeight}
          updateResultInfo={updateResultInfo}
        />
      </GroupPanel>
    ) : (
        <ReportViewerFilters
          accessPriority={accessPriority}
          afterToggled={this.updateViewerContentHeight}
          afterMove={this.updateViewerContentHeight}
          fieldStore={fieldsStore}
          configFields={fieldsStore.reportFilters.displayConfigFields}
          isLazyLoading
          isReport
          removeCrossFiltering={this.removeCrossFiltering}
          hadSetting={reportBody.hadCrossFilteringSetting}
          removeAllCrossFiltering={this.removeAllCrossFiltering}
          resetDefaultValue={this.resetDefaultValue}
          enableDeleteAll={reportBody.enableDeleteAll}
          crossFilteringDetails={reportBody.crossFilteringDetail}
          showCrossFilteringOption
          enableCrossFilteringOption={reportBody.enableCrossFiltering}
          updateResultInfo={updateResultInfo}
        />
      );
  }

  /**
   * Set ref to content
   * @param {Object} node
   */
  setViewerContentRef = (node: any): void => {
    this.viewerContentNode = node;
    this.updateViewerContentHeight();
  };

  /**
   * Render report viewer.
   */
  renderReportViewerMode(): any {
    const { store } = this.props;
    const isSubscriptionMode = get('model.displayMode.isSubscriptionMode', this.masterStore);
    if (isSubscriptionMode) {
      return null;
    }

    return (
      <ReportViewerContent
        setViewerContentRef={this.setViewerContentRef}
        className='content-container'
        bottom={this.footerHeight}
        {...this.props}
        store={this.reportDesignerFieldsStore}
        reportViewerStore={store}
        viewerContentHeight={this.viewerContentHeight}
      />
    );
  }

  /**
   * Render subscription.
   * @param {string} reportId
   */
  renderSubscriptionMode(reportId: string): any {
    const isSubscriptionMode = get('model.displayMode.isSubscriptionMode', this.masterStore);

    if (!isSubscriptionMode) {
      return null;
    }
    return (
      <div className='u_paddingLeftRight'>
        <ReportSubscribe
          reportId={reportId}
          reportType={this.props.store.reportType}
          subscribedInstancesStore={this.props.reportSubscriptionStore}
          reportStore={this.reportDesignerFieldsStore.reportStore}
        />
      </div>
    );
  }

  /**
   * Cancel Report
   */
  cancelReport = (): any => {
    const {
      loader,
      store,
      router,
      route: { name: reportName },
      params: { id: reportId },
      location: {
        query: { inheritFilter }
      }
    } = this.props;
    // Clear cached data report
    store.readReport(null);

    this.returnToReportDefinition(inheritFilter, reportName, reportId, router);
    // Cancel change remove draft
    loader.startLoading();
    return store.cancelChanges().then(() => {
      loader.endLoading();
      // Init report
      this.init(undefined, true);
    });
  };

  /**
   * Reset to original report if the report is inherited
   */
  resetReport = (): any => {
    const {
      store,
      router,
      route: { name: reportName },
      params: { id: reportId },
      location: {
        query: { inheritFilter }
      }
    } = this.props;
    // Clear cached data report
    store.readReport(null);

    this.returnToReportDefinition(inheritFilter, reportName, reportId, router);
    // Cancel change remove draft
    return store.cancelChanges().then(() => {
      // Init report
      this.init(undefined, true);
    });
  };

  /**
   * Set ref to report viewer
   * @param {Object} node
   */
  setViewerRef = (node: any): void => {
    this.viewerNode = node;
  };

  /**
   * Navigate to report definition
   * @param {string} inheritFilter
   * @param {string} reportName
   * @param {string} reportId
   * @param {any} router
   */
  returnToReportDefinition = (inheritFilter: string, reportName: string, reportId: string, router: any): any => {
    if (inheritFilter) {
      const reportUrl = routeUtils.getReportUrl(reportName, { id: reportId });
      router.push({ pathname: reportUrl });
    }
  };

  render() {
    const className = classnames('izenda-ReportViewer izenda-Wrapper', {
      hide: this.props.store.disabled
    });

    const errorKey = this.masterStore.alertDetails.key;
    return (
      <section className={className} ref={this.setViewerRef}>
        {isEqual(errorKey, 'report') ? this.renderErrorReport() : this.renderContent()}
      </section>
    );
  }

  /**
   * Render error report
   */
  renderErrorReport(): any {
    const handleClickDesign = () => {
      const reportId = this.masterStore.reportId;
      const viewReportDesignerUrl = routeUtils.getReportDesignerUrl('ReportDesignerDetail', { reportId: reportId });
      this.props.router.push(viewReportDesignerUrl);
    };

    const { authorization, localization } = this.props;
    const alertDetails = this.masterStore.alertDetails;
    const hasReportDesignerLicense = authorization.checkLicense(LicenseCheckerConstants.LICENSE_KEY.ReportDesigner);
    const accessPriority = this.masterStore.reportAccessPriority;

    return (
      <div className='u_table izenda-Wrapper'>
        <div className='u_tableCell u_alignMiddle u_textCenter'>
          <Alert type='error' className='white-space-pre-line align-left' autoHide={false}>
            {map(item => {
              return (
                <div key={item.message}>
                  <span>{item.message}</span>
                </div>
              );
            }, alertDetails.errors)}
          </Alert>
          {hasReportDesignerLicense &&
            (accessPriority === ACCESS_RIGHT.FullAccess || accessPriority === ACCESS_RIGHT.SaveAs) && (
              <div className='u_paddingTop'>
                <Button onClick={handleClickDesign}>{localization.getText('REPORT_LABEL_DESIGN')}</Button>
              </div>
            )}
        </div>
      </div>
    );
  }

  /**
   * Render content
   */
  renderContent(): any {
    const { localization, store, params } = this.props;
    const paramID = this.getParamID(params.id);
    const reportDesignerModel = this.masterStore.model;
    const fieldsStore = this.reportDesignerFieldsStore;
    const hideIntegratedButton = get('integrationStyle.hideIntegratedButton', this.props);
    const isIframeExported = routeUtils.checkIframeExported();
    const reportDetail = {
      type: store.reportType,
      name: reportDesignerModel.reportName,
      id: paramID,
      isGlobal: reportDesignerModel.isGlobal,
      version: reportDesignerModel.version
    };

    return (
      <div>
        {this.masterStore.disableReportDesigner && (
          <Alert type='error' autoHide={false}>
            {localization.getText('COMMON_MESSAGES_NOPERMISSION_VIEWREPORT')}
          </Alert>
        )}
        {isIframeExported || (this.masterStore.isCompleteLoad && !this.masterStore.disableReportDesigner) && (
          <ReportViewerToolbar
            {...this.props}
            store={store}
            reportId={paramID}
            fieldStore={fieldsStore}
            designerStore={this.masterStore}
            boardDetail={reportDetail}
            updateReportParts={this.updateReportParts(true)}
            cancelReport={this.cancelReport}
            hideIntegratedButton={hideIntegratedButton}
            resetReport={this.resetReport}
            isCacheEnable={this.isCacheEnable}
          />
        )}

        {this.masterStore.isCompleteLoad && !this.masterStore.disableReportDesigner && (
          <div className='izenda-Wrapper-content'>
            <div className='izenda-TableLayout'>
              <div className='izenda-TableLayout-Cell'>
                <div className='u_fullHeight'>
                  {!isIframeExported && (
                    <div className='u_paddingLeftRight'>
                      <ReportViewerHeader
                        {...this.props}
                        boardDetail={reportDetail}
                        fieldStore={fieldsStore}
                        updateReportName={reportDesignerModel.changeName.bind(reportDesignerModel)}
                        updateReportParts={this.updateReportParts(false)}
                      />
                      {this.renderReportFilter()}
                    </div>
                  )}
                  <div className='u_fullHeight'>
                    {this.renderReportViewerMode()}
                    {this.renderSubscriptionMode(paramID)}
                  </div>
                </div>
              </div>
              {this.props.editMode && (
                <RightContentFieldDetail fieldStore={fieldsStore} reportDesignerModel={reportDesignerModel} />
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./ui/modules/Reports/ReportViewer/Izenda.ReportViewer.js